import { useEffect, useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { Form, Formik } from "formik";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { EXCEL_FILE_SIZE, EXCEL_FILE_SUPPORTED_FORMATS, gridSpacing } from "../../../store/constants";
import SelectWrapper from "../../../utils/ui-components/FormsUI/Select";
import TextFieldWrapper from "../../../utils/ui-components/FormsUI/TextField";
import { UserService } from "../../../assets/_services/user-service";
import AutoCompleteSearchWrapper from "../../../utils/ui-components/FormsUI/AutoCompleteSearch";
import { useSelector } from "react-redux";
import { getState } from "../../../redux/actions/actions";
import { BulkSmsCampaignCreationValues } from ".";
import AddIcon from "@mui/icons-material/Add";
import EmptyResult from "../../../utils/ui-components/EmptyResult";
import Spinner from "../../../utils/ui-components/Spinner";
import TablePaginationActions from "@mui/material/TablePagination/TablePaginationActions";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { ContactService } from "../../../assets/_services/contact-service";
import ViewEditDialog from "../../../utils/ui-components/ViewEditDialog";
import AddContact from "../../Configurations/ContactList/add-contact";
import RadioGroupWrapper from "../../../utils/ui-components/FormsUI/RadioGroup";
import FileUpload from "../../../utils/ui-components/FormsUI/fileUpload/file-upload.component";
import SampleFileDowload from "../../../utils/ui-components/SampleFileDownload";
import * as XLSX from "xlsx";

// Load the plugins
dayjs.extend(utc);
dayjs.extend(timezone);

function BulkSmsCampaignSettings(props: {
  role: number;
  initialState: BulkSmsCampaignCreationValues;
  isLoadingMask: boolean;
  getChannelAndMaskList: (id: number | string) => void;
  setInitialState: (x: any) => void;
  handleNext: () => void;
  setSuffix: (x: any) => void;
  optOutTemplate: {
    dialog: string;
    nonDialog: string;
  };
  suffix: string;
  maskList: { value: number | string; label: string; type: number }[];
}) {
  const authState = useSelector((state: any) => state.auth.authData);
  const user = getState(authState);
  const theme: any = useTheme();
  const [open, setOpen] = useState(false);
  const formikRef: any = useRef(null);

  const [contactList, setContactList] = useState([]);
  const [isLoadingContactList, setIsloadingContactList] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [count, setCount] = useState(0);
  const [parentId, setParentId] = useState(0);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (props.role === 1 && parentId) {
      fetchContactList();
    }
    if (props.role === 2) {
      fetchContactList();
    }
  }, [page, pageSize, parentId]);

  const fetchContactList = () => {
    setIsloadingContactList(true);
    let formData: any = {
      contactListName: "",
    };
    if (props.role === 1) {
      formData = {
        parentId: parentId,
      };
    }
    ContactService.getContactList(formData, page, pageSize).then((response) => {
      if (response.isSuccess) {
        setContactList(response.data.data.rows);
        setCount(response.data.data.count);
      }
      setIsloadingContactList(false);
    });
  };

  const dialog = useMemo(
    () =>
      ViewEditDialog(AddContact)({
        open: open,
        setOpen: setOpen,
        dialogTitle: "Create Number Base",
        initialItem: {
          contactName: "",
          numberFile: [],
        },
        fetchData: fetchContactList,
        theme: theme,
        maxWidth: "md",
        initialData: { creator: parentId, role: props.role },
      }),
    [open]
  );

  const readFile = (files: any) => {
    if (files?.length) {
      const reader = new FileReader();
      reader.onload = (evt: any) => {
        // evt = on_file_select event
        /* Parse data */
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        /* Get first worksheet */
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        /* Convert array of arrays */
        // const data = XLSX.utils.sheet_to_json(ws);
        /* Update state */
        formikRef.current.setFieldValue("message", ws["B1"].v);
        // setMessage(ws["B1"].v);
      };
      reader.readAsBinaryString(files[0]);
    } else {
      formikRef.current.setFieldValue("message", "");
    }
  };
  return (
    <Grid container justifyContent="center" spacing={2}>
      <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
        <Box
          sx={{
            mt: 5,
            padding: "15px",
          }}
        >
          <Formik
            initialValues={{ ...props.initialState }}
            enableReinitialize
            innerRef={formikRef}
            validateOnMount
            validationSchema={Yup.object().shape({
              campaignName: Yup.string().required("Please enter Campaign Name"),
              mask: Yup.string().required("Please select mask"),
              parent_id: Yup.object().required("Please select parent user").typeError("Please select the parent user"),
              numberList: Yup.object().shape({
                numberListType: Yup.string().required("Number base type is required"),
                contactList: Yup.array()
                  .of(
                    Yup.object().shape({
                      id: Yup.string(),
                      name: Yup.string(),
                      cost: Yup.object(),
                    })
                  )
                  .test("contactList", "At least one contact list required", function (value: any) {
                    const { numberListType } = this.parent;

                    return Number(numberListType) === 1 ? value.length > 0 : true;
                  }),
                // .required("Please select contanct list")
                // .min(1, "Select at least one contact list"),
                file: Yup.mixed()
                  .nullable()
                  .test("required", "Number File is required", function (value: any) {
                    const { numberListType } = this.parent;
                    return Number(numberListType) === 1 ? true : value.length > 0;
                  })
                  .test("FILE_SIZE", "The size of uploaded files exceeds the maximum limit of 15MB.", (value: any) => {
                    return (
                      value?.reduce((accumulator: any, curValue: any) => {
                        return accumulator + curValue.size;
                      }, 0) <= EXCEL_FILE_SIZE
                    );
                  })
                  .test("FILE_FORMAT", "Only .xlsx, .xls, and .csv, files are allowed.", (value: any) =>
                    !value.length ? true : value?.filter((obj: any) => EXCEL_FILE_SUPPORTED_FORMATS.includes(`${obj?.type?.split(".")[0]}`)).length
                  ),
              }),
            })}
            onSubmit={(values: BulkSmsCampaignCreationValues, { setSubmitting, resetForm }) => {
              props.setInitialState(values);
              props.handleNext();
            }}
          >
            {({ values, isValid, setFieldValue }) => (
              <Form>
                <Grid container rowSpacing={1} columnSpacing={gridSpacing}>
                  {Number(user.role) === 1 && (
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <AutoCompleteSearchWrapper
                        // sx={{ mt: 1 }}
                        disabled={false}
                        freeSolo
                        name={"parent_id"}
                        label={"Parent User"}
                        data={values}
                        fetchData={async (value: any, data: any) => {
                          try {
                            let searchData: {
                              status: number | null;
                              userName: string;
                            };
                            if (
                              typeof data.parent_id === "object" &&
                              value.value === data.parent_id?.label &&
                              typeof data.parent_id?.value === "number"
                            ) {
                              props.getChannelAndMaskList(data.parent_id?.value);
                              setParentId(data.parent_id?.value);
                            } else if (value.value === "") {
                              setFieldValue("mask", "");
                              return { isSuccess: true, data: [] };
                            } else {
                              setFieldValue("mask", "");
                              searchData = {
                                status: 1,
                                userName: String(value?.value),
                              };
                              const response = await UserService.getAllParentListWithoutPagination(searchData);
                              if (response.isSuccess) {
                                const parents = response.data?.data?.map((e: any) => ({
                                  label: e.userName,
                                  value: e.id,
                                }));
                                return { isSuccess: true, data: parents };
                              } else {
                                return { isSuccess: false, data: [] };
                              }
                            }
                          } catch (error) {
                            return { isSuccess: false, data: [] };
                          }
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item lg={6} md={12} sm={12} xs={12}>
                    <TextFieldWrapper required fullWidth type="text" name="campaignName" label="Campaign Name" />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    {props.isLoadingMask ? (
                      <Skeleton variant="rounded" width={"100%"} height={40} animation={"wave"} sx={{ mt: 1 }} />
                    ) : (
                      <SelectWrapper
                        required
                        name="mask"
                        label="Mask"
                        disabled={props.maskList.length === 0 && Number(user.role) === 1}
                        options={props.maskList}
                        customHandleChange={(e: any) => {
                          setFieldValue("mask", e.target.value);
                          // messageCount({ ...values, mask: e.target.value });
                          const oldSuffix = `\n${props.optOutTemplate.dialog.replace(
                            "#_MASK",
                            props.maskList?.find((o: any) => o.value === values.mask)?.label ?? ""
                          )}`;
                          const newSuffix = `\n${props.optOutTemplate.dialog.replace(
                            "#_MASK",
                            props.maskList?.find((o: any) => o.value === e.target.value)?.label ?? ""
                          )}`;

                          if (props.maskList.find((o: any) => o.value === e.target.value)?.type === 1) {
                            if (values.message.includes(oldSuffix)) setFieldValue("message", values.message.replace(props.suffix, newSuffix));
                            else setFieldValue("message", values.message + newSuffix);
                            props.setSuffix(newSuffix);
                          } else {
                            if (values.message.includes(oldSuffix)) setFieldValue("message", values.message.replace(props.suffix, ""));
                            else setFieldValue("message", values.message);
                            props.setSuffix("");
                          }
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item lg={12} md={12} xs={12}>
                    <RadioGroupWrapper
                      required
                      name="numberList.numberListType"
                      label="Number base Type"
                      radioGroup={[
                        {
                          value: 1,
                          label: "Number List",
                        },
                        {
                          value: 2,
                          label: "Personalized",
                        },
                        // {
                        //   value: 3,
                        //   label: "Template",
                        // },
                      ]}
                      disabled={false}
                    />
                  </Grid>
                  {Number(values.numberList.numberListType) === 1 && (
                    <>
                      <Grid item xs={12}>
                        <Stack
                          direction={"row"}
                          justifyContent={values.numberList?.contactList?.length === 0 ? "space-between" : "flex-end"}
                          alignItems={"flex-end"}
                        >
                          {Number(values.numberList.numberListType) === 1 && values.numberList.contactList.length === 0 && (
                            <Typography color={"error"} variant="caption">
                              Select at least one number list
                            </Typography>
                          )}
                          {props.role === 2 && (
                            <Button
                              startIcon={<AddIcon />}
                              variant="contained"
                              onClick={() => {
                                setOpen(true);
                              }}
                            >
                              Number List
                            </Button>
                          )}
                        </Stack>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ overflowX: "auto" }}>
                          <Table width="100%">
                            <TableHead>
                              <TableRow>
                                <TableCell width={"50px"} />
                                <TableCell>Name</TableCell>
                                <TableCell width={"150px"}>Number Count</TableCell>
                                <TableCell width={"200px"}>Created Time</TableCell>
                                <TableCell width={"200px"}>Updated Time</TableCell>
                                <TableCell>Description</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {!isLoadingContactList &&
                                contactList.map((contact: any, index) => (
                                  <TableRow key={index}>
                                    <TableCell width={50}>
                                      <Checkbox
                                        sx={{ p: 0, m: 0 }}
                                        checked={values?.numberList?.contactList?.some((cont) => cont.id === contact.id)}
                                        onClick={() => {
                                          if (!values?.numberList?.contactList?.some((cont) => cont.id === contact.id)) {
                                            setFieldValue("numberList.contactList", [
                                              ...values?.numberList?.contactList,
                                              {
                                                id: contact.id,
                                                name: contact.contact_list_name,
                                                cost: {
                                                  dialog_numbers: contact.dialog_numbers,
                                                  mobitel_numbers: contact.mobitel_numbers,
                                                  hutch_numbers: contact.hutch_numbers,
                                                  airtel_numbers: contact.airtel_numbers,
                                                },
                                              },
                                            ]);
                                          } else {
                                            setFieldValue(
                                              "numberList.contactList",
                                              values?.numberList?.contactList?.filter((cont) => cont.id !== contact.id)
                                            );
                                          }
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>{contact?.contact_list_name || ""}</TableCell>
                                    <TableCell>{contact?.valid_numbers || ""}</TableCell>
                                    <TableCell>{contact?.created_at ? dayjs(contact?.created_at).format("DD MMM YYYY hh:mm:ss a") : "-"}</TableCell>
                                    <TableCell>{contact?.updated_at ? dayjs(contact?.updated_at).format("DD MMM YYYY hh:mm:ss a") : "-"}</TableCell>
                                    <TableCell>{contact?.description || ""}</TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </Box>
                        {!contactList.length && !isLoadingContactList && <EmptyResult />}
                        {isLoadingContactList && (
                          <Box
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexDirection: "column",
                              minHeight: "40vh",
                            }}
                          >
                            <Spinner />
                          </Box>
                        )}
                        <TablePagination
                          rowsPerPageOptions={[5, 10, 25, 50]}
                          component="div"
                          count={count}
                          rowsPerPage={pageSize}
                          page={page}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                          ActionsComponent={TablePaginationActions}
                        />
                      </Grid>
                    </>
                  )}
                  {Number(values.numberList.numberListType) === 2 && (
                    <>
                      <Grid item lg={8} md={8} sm={8} xs={12}>
                        <FileUpload
                          accept=".xls,.xlsx,text/csv"
                          label="Number File Upload"
                          maxFileSizeInBytes={EXCEL_FILE_SIZE}
                          name="numberList.file"
                          customHandleChange={readFile}
                          limit={"15 MB"}
                        />
                      </Grid>
                      <Grid item lg={4} md={4} sm={4} xs={12}>
                        <SampleFileDowload type={2} />
                      </Grid>
                    </>
                  )}
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      py: 2,
                    }}
                  >
                    <Box sx={{ width: 10 }} />
                    <Button color="primary" variant="contained" type="submit" endIcon={<NavigateNextIcon />} disabled={!isValid}>
                      Next
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Box>
      </Grid>
      {dialog}
    </Grid>
  );
}

export default BulkSmsCampaignSettings;
